import type { FC } from "react"
import { useMemo, useCallback } from "react"

import { Modal, Button } from "react-materialize"

import { useAppDispatch, useAppSelector } from "@/app/hooks"

import {
  setPropzMediaStateParameter,
} from "@/features/propzMedia/propzMediaSlice"

import { format } from 'date-fns'
import { parseDate } from '@assets/utils'

import './style.sass'

import { formatPriceToBr } from "@/utils"

import { brazilian_text_to_searchable } from "@assets/functions"
import { formatPrice } from '@assets/utils/formatPrice'

import { ReactComponent as ArrowDownSVG } from './ArrowDown.svg'
import { ReactComponent as CancelSVG } from './Cancel.svg'
import { ReactComponent as DoneSVG } from './Done.svg'
import { ReactComponent as ExclamationSVG } from './Exclamation.svg'
import { ReactComponent as WatchSVG } from './Watch.svg'

import type { CreditOperation, TransactionResponse } from '@assets/types'

// Constantes
const DEBIT_TYPES = {
  USAGE: 'USAGE',
  EXPIRATION: 'EXPIRATION',
  EXPIRED: 'EXPIRED',
} as const

const OPERATION_TYPES = {
  DEBIT: 'DEBIT',
} as const

const CREDIT_STATUS = {
  APPROVED: 'approved',
  PENDING: 'pending',
} as const

const MIN_AMOUNT_THRESHOLD = 0.01

// Tipos auxiliares
type CreditRedeem = {
  amount: number
  debitType?: string
  dateCreated?: string
  operation?: {
    type?: string
    ticketJson?: string
    ticket?: {
      amountWithAllDiscount?: number
      date?: string
    }
  }
}

type ExtendedCreditOperation = CreditOperation & {
  advertiserId?: number | string
  dateCreated?: string | Date
}

type ExtendedTransaction = TransactionResponse & {
  amount?: number
  creditRedeems?: CreditRedeem[] | null
  creditOperation: ExtendedCreditOperation
  creditDateCreated?: string | Date
  approvalPrevisionDate?: string | Date | null
  dateUpdated?: string
}

type TicketData = {
  ticket?: {
    amountWithAllDiscount?: number
    date?: string
  }
}

// Funções auxiliares
const checkCreditOperationStatus = (creditOperation: CreditOperation, status: string): boolean => {
  const commissionStatus = brazilian_text_to_searchable(creditOperation.commissionStatus ?? '')
  const creditStatus = brazilian_text_to_searchable(creditOperation.creditStatus ?? '')
  return commissionStatus === status || creditStatus === status
}

const isTransactionExpired = (transaction: ExtendedTransaction): boolean => {
  const firstCreditRedeem = transaction.creditRedeems?.[0]
  if (!firstCreditRedeem) return false
  
  return (
    firstCreditRedeem.debitType === DEBIT_TYPES.EXPIRATION ||
    firstCreditRedeem.debitType === DEBIT_TYPES.EXPIRED
  )
}

const isTransactionUsed = (transaction: ExtendedTransaction): boolean => {
  const firstCreditRedeem = transaction.creditRedeems?.[0]
  if (!firstCreditRedeem) return false
  
  return (
    firstCreditRedeem.debitType === DEBIT_TYPES.USAGE &&
    firstCreditRedeem.operation?.type === OPERATION_TYPES.DEBIT
  )
}

const parseTicketJson = (ticketJson?: string): TicketData | null => {
  if (!ticketJson) return null
  
  try {
    return JSON.parse(ticketJson)
  } catch {
    return null
  }
}

const formatDate = (date: string | Date | undefined, fallback = ''): string => {
  if (!date) return fallback
  
  try {
    const parsedDate = typeof date === 'string' ? parseDate(date) : date
    return format(parsedDate as Date, 'dd/MM/yyyy')
  } catch {
    return fallback
  }
}

export const CashbackModal: FC = () => {
  const dispatch = useAppDispatch()

  const {
    showCashbackModal,
    transactions,
    advertiser
  } = useAppSelector((state) => state.propzMedia)

  const handleCloseModal = useCallback(() => {
    dispatch(setPropzMediaStateParameter({ showCashbackModal: false }))
  }, [dispatch])

  const statements = useMemo(() => {
    const final: ExtendedTransaction[] = []
    
    if (!transactions) return final

    try {
      transactions.forEach((transaction: TransactionResponse) => {
        const creditRedeems = transaction.creditRedeems as CreditRedeem[] | null | undefined

        if (creditRedeems && creditRedeems.length > 1) {
          creditRedeems.forEach((credit: CreditRedeem) => {
            const amount = Number(credit.amount)
            
            if (amount >= MIN_AMOUNT_THRESHOLD) {
              const creditCopy = JSON.parse(JSON.stringify(credit)) as CreditRedeem
              
              if (credit.operation?.ticketJson) {
                creditCopy.operation = {
                  ...creditCopy.operation,
                  ticket: parseTicketJson(credit.operation.ticketJson)?.ticket
                }
              }
              
              final.push({
                ...transaction,
                creditRedeems: [creditCopy]
              } as ExtendedTransaction)
            }
          })
        } else {
          final.push(transaction as ExtendedTransaction)
        }
      })
      
      return final
    } catch (error) {
      console.error('Erro ao processar transações:', error)
      return final
    }
  }, [transactions])

  const renderTransactionStatus = useCallback((transaction: ExtendedTransaction) => {
    if (isTransactionUsed(transaction)) {
      return (
        <>
          <ArrowDownSVG /> Utilizado
        </>
      )
    }

    if (isTransactionExpired(transaction)) {
      return (
        <>
          <WatchSVG /> Expirado
        </>
      )
    }

    if (checkCreditOperationStatus(transaction.creditOperation, CREDIT_STATUS.APPROVED)) {
      return (
        <>
          <DoneSVG /> Recebido
        </>
      )
    }

    if (checkCreditOperationStatus(transaction.creditOperation, CREDIT_STATUS.PENDING)) {
      return (
        <>
          <ExclamationSVG /> A receber
        </>
      )
    }

    return (
      <>
        <CancelSVG /> Cancelado
      </>
    )
  }, [])

  const renderTransactionDateInfo = useCallback((transaction: ExtendedTransaction) => {
    const firstCreditRedeem = transaction.creditRedeems?.[0]

    if (isTransactionUsed(transaction)) {
      return (
        <>
          Data da utilização: {formatDate(firstCreditRedeem?.dateCreated)}
        </>
      )
    }

    if (isTransactionExpired(transaction)) {
      return (
        <>
          Data da expiração: {formatDate(firstCreditRedeem?.dateCreated)}
        </>
      )
    }

    if (checkCreditOperationStatus(transaction.creditOperation, CREDIT_STATUS.APPROVED)) {
      return (
        <>
          Data de aprovação: {formatDate(transaction.creditDateCreated)}
        </>
      )
    }

    if (checkCreditOperationStatus(transaction.creditOperation, CREDIT_STATUS.PENDING)) {
      const approvalDate = transaction.approvalPrevisionDate
      return (
        <>
          Data provável de aprovação: {approvalDate ? formatDate(approvalDate) : ''}
        </>
      )
    }

    return (
      <>
        Data do cancelamento: {formatDate(transaction.expirationCreditDate)}
      </>
    )
  }, [])

  const getStoreName = useCallback((advertiserId: number | string): string => {
    const advertiserData = advertiser.find(
      (store) => store.advertiserId === Number(advertiserId)
    )
    return advertiserData?.name ? ` na ${advertiserData.name}` : ''
  }, [advertiser])

  const getTransactionAmount = useCallback((transaction: ExtendedTransaction): number => {
    return transaction.creditRedeems?.[0]?.amount || transaction.amount || 0
  }, [])

  const getPurchaseAmount = useCallback((transaction: ExtendedTransaction): number => {
    const ticketData = parseTicketJson(transaction.creditOperation?.ticketJson)
    const ticketAmount = ticketData?.ticket?.amountWithAllDiscount
    const saleAmount = transaction.creditOperation?.saleAmount?.amount
    
    return ticketAmount || saleAmount || 0
  }, [])

  const getPurchaseDate = useCallback((transaction: ExtendedTransaction): string => {
    const ticketData = parseTicketJson(transaction.creditOperation?.ticketJson)
    const ticketDate = ticketData?.ticket?.date
    
    if (ticketDate) {
      try {
        return formatDate(new Date(ticketDate))
      } catch {
        return ''
      }
    }
    
    const dateCreated = transaction.creditOperation?.dateCreated
    if (dateCreated) {
      return formatDate(typeof dateCreated === 'string' ? dateCreated : dateCreated.toString())
    }
    
    return ''
  }, [])

  const modalOptions = useMemo(() => ({
    dismissible: true,
    endingTop: "10%",
    inDuration: 250,
    opacity: 0.5,
    outDuration: 250,
    preventScrolling: true,
    startingTop: "4%",
    onCloseEnd: handleCloseModal,
  }), [handleCloseModal])

  return (
    <Modal
      actions={[]}
      bottomSheet
      id="cashbackModal"
      open={showCashbackModal}
      options={modalOptions}
    >
      <h4 className="header">
        Meu extrato
        <Button
          flat
          waves="cyan"
          className="white stores-close-button"
          onClick={handleCloseModal}
        >
          <i className="material-icons">close</i>
        </Button>
      </h4>

      <h5 className="saldo_atual">
        Saldo atual:{' '}
        <span>
          {formatPriceToBr(window.personalBalance)}
        </span>
      </h5>

      <ul className="collection">
        {statements.map((transaction) => {
          const transactionKey = transaction.creditOperation?.ticketId || 
                                transaction.creditOperation?.promotionId || 
                                `${transaction.creditDateCreated}-${transaction.creditOperation?.amount}`
          
          return (
            <li key={transactionKey} className="collection-item">
              <div className="line">
                Cashback:{' '}
                <span className="amount">
                  {formatPrice(getTransactionAmount(transaction))}
                </span>
                {renderTransactionStatus(transaction)}
              </div>

              {transaction.creditOperation.ticketJson && (
                <div className="line">
                  Compra
                  {transaction.creditOperation.advertiserId && 
                    getStoreName(transaction.creditOperation.advertiserId)}
                  {' de '}
                  {formatPriceToBr(getPurchaseAmount(transaction))}
                  {' realizada em '}
                  {getPurchaseDate(transaction)}
                </div>
              )}

              {!transaction.creditOperation.ticketJson && !transaction.creditOperation.storeId && (
                <div className="line">
                  Cashback crédito
                </div>
              )}

              <div className="line">
                {renderTransactionDateInfo(transaction)}
              </div>
            </li>
          )
        })}
      </ul>
    </Modal>
  )
}